import { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

/**
 * Shared base modal. Renders into a portal on document.body.
 *
 * Props:
 *   isOpen       — whether the modal is visible
 *   onClose      — called when ESC pressed or overlay clicked
 *   title        — optional heading rendered above children
 *   className    — extra class(es) appended to the inner .modal panel
 *   overlayClassName — extra class(es) on the overlay (e.g. site-update-loading-overlay)
 *   children     — modal body content
 */
export default function Modal({ isOpen, onClose, title, className = '', overlayClassName = '', style, children }) {
  const panelRef = useRef(null);
  const returnFocusRef = useRef(null);
  // Keep latest onClose without re-binding the keydown listener / focus effect on every parent render.
  // Unstable onClose (e.g. inline resetForm) previously re-ran the open effect and stole focus
  // back to the first field on every keystroke.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      onCloseRef.current();
      return;
    }

    if (e.key === 'Tab') {
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTORS));
      if (focusable.length === 0) { e.preventDefault(); return; }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    returnFocusRef.current = document.activeElement;
    document.addEventListener('keydown', handleKeyDown);

    const panel = panelRef.current;
    if (panel) {
      const first = panel.querySelector(FOCUSABLE_SELECTORS);
      if (first) {
        first.focus();
      } else {
        panel.focus();
      }
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (returnFocusRef.current && typeof returnFocusRef.current.focus === 'function') {
        returnFocusRef.current.focus();
      }
    };
  }, [isOpen, handleKeyDown]);

  if (!isOpen) return null;

  const overlayClass = ['modal-overlay', overlayClassName].filter(Boolean).join(' ');
  const panelClass = ['modal', className].filter(Boolean).join(' ');

  return createPortal(
    <div
      className={overlayClass}
      onClick={() => onCloseRef.current()}
      role="presentation"
    >
      <div
        ref={panelRef}
        className={panelClass}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title || undefined}
        tabIndex={-1}
        style={style}
      >
        {title && <h2>{title}</h2>}
        {children}
      </div>
    </div>,
    document.body,
  );
}
